import { Component, OnDestroy, OnInit } from '@angular/core';
import { ModalController } from 'ionic-angular';
import { Subscription } from 'rxjs/Subscription';
import { SupportContext, SupportContextKey, SupportTrainingMenuItem } from '../../../../models/support/support.models';
import { SupportContextService } from '../../../../services/support/support-context.service';
import { SupportVideoCatalogService } from '../../../../services/support/support-video-catalog.service';
import {
  ServiceDeskTicketModalComponent
} from '../service-desk-ticket-modal/service-desk-ticket-modal.component';

@Component({
  selector: 'support-panel',
  templateUrl: 'support-panel.component.html'
})
export class SupportPanelComponent implements OnInit, OnDestroy {
  expanded: boolean = false;
  context: SupportContext = new SupportContext();
  suggestedVideos: SupportTrainingMenuItem[] = [];
  otherVideos: SupportTrainingMenuItem[] = [];
  filteredOtherVideos: SupportTrainingMenuItem[] = [];
  allVideosSearchTerm: string = '';
  private contextSubscription: Subscription;

  constructor(
    private modalController: ModalController,
    private supportContextService: SupportContextService,
    private supportVideoCatalogService: SupportVideoCatalogService
  ) {
  }

  ngOnInit() {
    this.contextSubscription = this.supportContextService.currentContext.subscribe(context => {
      this.context = context || new SupportContext();

      if (this.isLoginContext()) {
        this.expanded = false;
      }

      const additionalContextKeys = this.context.additionalSuggestedContextKeys || [];
      this.suggestedVideos = this.supportVideoCatalogService.getSuggestedTrainingItems(this.context.key, additionalContextKeys);
      this.otherVideos = this.supportVideoCatalogService.getOtherTrainingItems(this.context.key, additionalContextKeys);
      this.filteredOtherVideos = this.otherVideos;
      this.allVideosSearchTerm = '';
    });
  }

  ngOnDestroy() {
    if (this.contextSubscription) {
      this.contextSubscription.unsubscribe();
    }
  }

  get tabLabel(): string {
    return this.context && this.context.key === SupportContextKey.Login
      ? 'TROUBLE LOGGING IN?'
      : 'SUPPORT';
  }

  get tabTitle(): string {
    if (this.isLoginContext()) {
      return 'Raise login support ticket';
    }

    return this.expanded ? 'Close support panel' : this.tabLabel;
  }

  onAllVideosSearchChange(searchTerm: string) {
    const normalisedSearchTerm = (searchTerm || '').trim().toLowerCase();

    if (!normalisedSearchTerm) {
      this.filteredOtherVideos = this.otherVideos;
      return;
    }

    this.filteredOtherVideos = this.otherVideos.filter(video => this.fuzzyMatches(video.title, normalisedSearchTerm));
  }

  async toggle() {
    if (this.isLoginContext()) {
      this.expanded = false;
      await this.openTicketModal();
      return;
    }

    this.expanded = !this.expanded;

    if (this.expanded) {
      await this.supportContextService.refreshCurrentContextDetails();
    }
  }

  async openTicketModal() {
    this.context = await this.supportContextService.refreshCurrentContextDetails();

    const modal = this.modalController.create(
      ServiceDeskTicketModalComponent,
      {context: this.context},
      {cssClass: 'service-desk-ticket-modal'}
    );

    modal.present();
  }

  openVideo(item: SupportTrainingMenuItem) {
    const url = this.supportVideoCatalogService.getVideoPlayerUrl(item);

    if (!url) {
      return;
    }

    window.open(url, 'supportVideoPlayer', 'noopener,width=1280,height=800');
  }

  getTimeLabel(item: SupportTrainingMenuItem): string {
    if (!item) {
      return '';
    }

    if (!item.endTime) {
      return this.formatTime(item.startTime);
    }

    return `${this.formatTime(item.startTime)} - ${this.formatTime(item.endTime)}`;
  }

  private isLoginContext(): boolean {
    return this.context && this.context.key === SupportContextKey.Login;
  }

  private fuzzyMatches(value: string, searchTerm: string): boolean {
    const target = (value || '').toLowerCase();

    if (target.indexOf(searchTerm) !== -1) {
      return true;
    }

    let searchIndex = 0;

    for (let i = 0; i < target.length && searchIndex < searchTerm.length; i++) {
      if (target[i] === searchTerm[searchIndex]) {
        searchIndex++;
      }
    }

    return searchIndex === searchTerm.length;
  }

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) {
      return '00:00';
    }

    const wholeSeconds = Math.floor(seconds);
    const minutes = Math.floor(wholeSeconds / 60);
    const remainingSeconds = wholeSeconds % 60;

    return `${this.padTime(minutes)}:${this.padTime(remainingSeconds)}`;
  }

  private padTime(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}



// WEBPACK FOOTER //
// ./src/pages/support/components/support-panel/support-panel.component.ts